import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wrench } from 'lucide-react';
import type { LoRaDevice } from '../types';

interface DeviceTableProps {
  devices: LoRaDevice[];
  onCalibrate?: (device: LoRaDevice) => void;
  showCalibrate?: boolean;
}

const ALL = '__all__';

function formatTimestamp(ts: number | null): string {
  if (!ts) return '-';
  const date = new Date(ts * 1000);
  return date.toLocaleString('en-CA', { timeZone: 'America/Montreal' });
}

export function DeviceTable({ devices, onCalibrate, showCalibrate = true }: DeviceTableProps) {
  const [methodFilter, setMethodFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);

  const methods = useMemo(() => {
    const set = new Set<string>();
    for (const d of devices) {
      if (d.downlinkMethod) set.add(d.downlinkMethod);
    }
    return Array.from(set).sort();
  }, [devices]);

  const filtered = useMemo(() => {
    return devices.filter((d) => {
      if (methodFilter !== ALL && (d.downlinkMethod ?? '') !== methodFilter) return false;
      if (statusFilter === 'active' && !d.activated) return false;
      if (statusFilter === 'inactive' && d.activated) return false;
      return true;
    });
  }, [devices, methodFilter, statusFilter]);

  if (devices.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No LoRaWAN devices found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All methods</SelectItem>
            {methods.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        {(methodFilter !== ALL || statusFilter !== ALL) && (
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {devices.length} devices
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No devices match the selected filters
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>DevEUI</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Seen</TableHead>
              <TableHead>Wireless Device ID</TableHead>
              <TableHead>Interval (min)</TableHead>
              {showCalibrate && <TableHead className="w-[100px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((device) => (
              <TableRow key={device.devEui}>
                <TableCell className="font-mono text-sm">{device.devEui}</TableCell>
                <TableCell>{device.name || '-'}</TableCell>
                <TableCell>
                  {device.downlinkMethod ? (
                    <Badge variant="outline">{device.downlinkMethod}</Badge>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={device.activated ? 'default' : 'secondary'}>
                    {device.activated ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>{formatTimestamp(device.lastTimestamp)}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {device.wirelessDeviceId || '-'}
                </TableCell>
                <TableCell>{device.reportingIntervalMinutes ?? '-'}</TableCell>
                {showCalibrate && (
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onCalibrate?.(device)}
                    >
                      <Wrench className="h-4 w-4 mr-1" />
                      Calibrate
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
